import {
	Cookie,
	CookieService,
	FilterOperators,
	FilterTypes,
	Meta,
	PropertyFilter,
	Response,
	Role,
	Vehicle,
} from '@agilox/common';
import {
	ButtonComponent,
	DropdownDirective,
	DropdownModule,
	IconModule,
	InputComponent,
	InputModule,
	NotifyComponent,
	PillComponent,
	SelectModule,
	SpinnerComponent,
	TooltipModule,
} from '@agilox/ui';
import { AsyncPipe, NgClass } from '@angular/common';
import {
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	linkedSignal,
	model,
	output,
	signal,
	untracked,
	viewChild,
	WritableSignal,
	ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormValueControl } from '@angular/forms/signals';
import { TranslateModule } from '@ngx-translate/core';
import {
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	pairwise,
	startWith,
	switchMap,
	tap,
} from 'rxjs';
import { VehicleSelectOptionGroup } from '../shared/models/vehicle-select-option-group.interface';
import { UnionSelectOptionComponent } from '../shared/union-select-option/union-select-option.component';
import { VehicleSelectOptionComponent } from '../shared/vehicle-select-option/vehicle-select-option.component';
import { VehicleSelectPipe } from './pipes/vehicle-select.pipe';
import { VehiclesSelectService } from './vehicles-select.service';

const MAX_RECENT_SEARCHES = 10;

interface RecentSearch {
	term: string;
	timestamp: number;
}

@Component({
	selector: 'vehicles-select',
	templateUrl: './vehicles-select.component.html',
	providers: [VehiclesSelectService],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		SelectModule,
		TranslateModule,
		AsyncPipe,
		VehicleSelectPipe,
		DropdownModule,
		NgClass,
		PillComponent,
		IconModule,
		UnionSelectOptionComponent,
		VehicleSelectOptionComponent,
		NotifyComponent,
		InputModule,
		IconModule,
		TooltipModule,
		SpinnerComponent,
		ButtonComponent,
	],
})
export class VehiclesSelectComponent implements FormValueControl<Vehicle[]> {
	private vehiclesSelectService = inject(VehiclesSelectService);
	private cookieService = inject(CookieService);

	selectedSerials = input<string[]>([]);
	maxSelections = input<number>(20);
	maxSelectedVehiclesText = input<string>('vehicle_select.max');
	fullDropdownWidth = input<boolean>(true);
	excludedSerials = input<string[] | undefined>([]);
	excludedVehicleIds = input<string[]>([]);
	displayVehicleModel = input<boolean>(true);
	displayLabel = input<boolean>(true);
	placeholder = input<string>('select.placeholder');

	/**
	 * Temporary, needed to determine which details to show in the vehicle select
	 * Should be handled by injecting the auth service and getting the user role from there
	 * This is not possible at the moment, because every app has its own auth service
	 *
	 * We set it to default to support, because then we do not need to add the
	 * role everywhere we use the vehicle select
	 */
	role = input<Role>(Role.support);
	disabled = input<boolean>(false);
	invalid = input<boolean>(false);
	touched = input<boolean>(false);

	value = model<Vehicle[]>([]);

	vehicleSelected = output<Vehicle[]>();
	touch = output<void>();

	optionsList = viewChild<ElementRef<HTMLElement> | undefined>('optionsList');
	dropdown = viewChild<DropdownDirective | undefined>(DropdownDirective);
	searchInput = viewChild<InputComponent | undefined>('searchInput');

	showRecentSearches = computed(() => {
		return this.dropdownOpen() && this.recentSearches().length > 0 && this.recentSearchesOpen();
	});

	public searchOpen = computed(() => this.dropdownOpen());

	isDisabled = computed(() => this.disabled());

	isDisabledStyle = computed(
		() =>
			this.maxSelections() < this.selectedVehicles().length ||
			this.selectedVehicles().length === 0 ||
			this.loading()
	);

	/**
	 * Need to cache the selectedVehicles on the initial write
	 * otherwise not all vehicles will be selected.
	 */
	selectedVehicles = signal<Vehicle[]>([]);

	recentSearchesOpen = signal<boolean>(false);
	recentSearches = signal<string[]>([]);

	/**
	 * Interop the selectedVehicles signal to an observable
	 * to be used by parents until they can use the signal directly
	 */
	selectedVehicles$: Observable<Vehicle[]> = toObservable(this.selectedVehicles);

	public loading: WritableSignal<boolean> = signal(false);

	public focused = signal<boolean>(false);

	private page: Meta = { number: 0, size: 50 };
	private currentPage: number = 1;

	private pagination = signal<Meta>({
		number: 0,
		size: 50,
		total: 0,
	});

	public dropdownOpen = signal<boolean>(false);

	searchText = signal<string>('');

	private searchObservable$: Observable<string> = toObservable(this.searchText).pipe(
		startWith(''),
		takeUntilDestroyed(),
		tap((value) => {
			// Hide recent searches when user types
			if (value && value.length > 0) {
				this.recentSearchesOpen.set(false);
			}
		}),
		debounceTime(300),
		distinctUntilChanged(),
		// only set the loading when searched is triggered, not when scrolling
		tap(() => this.loading.set(true))
	);

	initialLoad = signal<boolean>(true);

	private _selectedUnions = linkedSignal(() => {
		const vehicles = this.selectedVehicles();
		if (!vehicles || vehicles.length === 0) {
			return [];
		}
		const selectedUnions = vehicles.map((vehicle) => vehicle.unionUuid);
		return Array.from(new Set(selectedUnions));
	});

	private checkNeedToFetchVehicles = (previous: string[], current: string[]) => {
		/**
		 * It can be marked as distinct in the following cases:
		 * length is the same and all values are the same
		 *
		 * previous is longer than current and current is a subset of previous
		 */
		if (previous.length === current.length) {
			return previous.every((value, index) => value === current[index]);
		}
		/** if previous is longer than current and current is a subset of previous **/
		if (previous.length > current.length) {
			return current.every((value) => previous.includes(value));
		}
		return false;
	};

	private selectedUnions$: Observable<string[]> = toObservable(this._selectedUnions).pipe(
		pairwise(),
		filter(([previous, current]) => !this.checkNeedToFetchVehicles(previous, current)),
		map(([_, current]) => current),
		startWith([]),
		tap(() => this.loading.set(true))
	);

	/**
	 * If a user selects "Select all from union"
	 * We need to fetch all the vehicles from the union
	 * before we can select them
	 * @private
	 */
	private cachedUnionToSelect: string | undefined;

	constructor() {
		this.recentSearches.set(this.loadRecentSearches());

		effect(() => {
			const vehicles = this.value();
			untracked(() => this.selectedVehicles.set(vehicles ?? []));
		});
	}

	public isUnionSelected(union: VehicleSelectOptionGroup): boolean {
		return this._selectedUnions().some((selectedUnion) => selectedUnion === union.uuid);
	}

	public isVehicleSelected(vehicle: Vehicle | undefined): boolean {
		return !!this.selectedVehicles()?.find((v: Vehicle) => v.serial === vehicle?.serial);
	}

	public onSelect(vehicle: Vehicle) {
		const alreadySelected = this.selectedVehicles()?.find(
			(v: Vehicle) => v.serial === vehicle.serial
		);
		if (alreadySelected) {
			this.selectedVehicles.set(
				this.selectedVehicles().filter((v: Vehicle) => v.serial !== vehicle.serial)
			);
		} else {
			this.selectedVehicles.set([...this.selectedVehicles(), vehicle]);
		}
	}

	private nextPage() {
		this.currentPage++;
		const nextSize = 50 * this.currentPage;
		if (nextSize < (this.page?.total || 0)) {
			this.pagination.set({ ...this.page, size: nextSize });
		}
	}

	public onDropdownStateChange(open: boolean) {
		this.dropdownOpen.set(open);
		if (open) {
			this.selectedVehicles.set(this.value() || []);
			this.focusSearchInput();
		} else {
			this.recentSearchesOpen.set(false);
			this.onTriggerBlur();
			this.focusTrigger();
		}
	}

	public onDeselectAll() {
		this.selectedVehicles.set([]);
	}

	public onDeselectAllInUnion(uuid: string) {
		this.selectedVehicles.set(
			this.selectedVehicles().filter((vehicle) => vehicle.unionUuid !== uuid)
		);
	}

	public onSelectAllInUnion(uuid: string) {
		/**
		 * Fetch all the vehicles from the union
		 * then select them
		 */
		this.cachedUnionToSelect = uuid;
		this._selectedUnions.set([...this._selectedUnions(), uuid]);

		// clear the search
		this.searchText.set('');
	}

	public onToggleUnion(union: VehicleSelectOptionGroup) {
		union.collapsed = !union.collapsed;
	}

	private getExclusionFilter(property: string, values: string[]): PropertyFilter | null {
		if (!values?.length) {
			return null;
		}
		return {
			property,
			operator: FilterOperators.UNEQUAL,
			value: values.join(','),
			type: FilterTypes.TEXT,
		};
	}

	public vehicleResponse$: Observable<Vehicle[]> = combineLatest([
		this.searchObservable$,
		toObservable(this.pagination),
		this.selectedUnions$,
		toObservable(this.excludedSerials),
		toObservable(this.excludedVehicleIds),
	]).pipe(
		debounceTime(300),
		switchMap(([search, page, unions, serials, vehicleIds]) => {
			const filter = [
				this.getExclusionFilter('serial', serials || []),
				this.getExclusionFilter('vehicle_uuid', vehicleIds),
			].filter((entry): entry is PropertyFilter => entry !== null);
			return this.vehiclesSelectService
				.fetchVehicles(search, page, this.selectedSerials(), unions, filter)
				.pipe(map((response) => this.sortOptions(response)));
		}),
		tap((data) => {
			this.page = data.meta;

			if (this.initialLoad()) {
				this.initialLoad.set(false);
				this.setVehiclesToSelectedVehiclesFromSerialStrings(this.selectedSerials(), data.data);
				this.focusFirstOption();
			} else if (this.cachedUnionToSelect) {
				this.setVehiclesFromCachedUnion(data.data);
			}
			this.loading.set(false);
		}),
		map((data) => data.data)
	);

	private setVehiclesFromCachedUnion(vehicles: Vehicle[]) {
		const vehiclesFromUnion = vehicles.filter(
			(vehicle) =>
				vehicle.unionUuid === this.cachedUnionToSelect && !this.isVehicleSelected(vehicle)
		);

		this.selectedVehicles.set([...this.selectedVehicles(), ...vehiclesFromUnion]);
		this.cachedUnionToSelect = undefined;
	}

	onScroll(): void {
		const element = this.optionsList()?.nativeElement;
		if (!element) return;
		const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 2;
		if (atBottom && !this.loading()) {
			this.nextPage();
		}
	}

	/**
	 * Needed in cases where the selected vehicles are passed as serials
	 * @example
	 * We get 123481723,1230129384
	 * When we receive the vehicles from the backend, we need to set the vehicles that have the serials 123481723 and 1230129384
	 * to the form control
	 *
	 * @param serials
	 * @param vehicles
	 * @private
	 */
	private setVehiclesToSelectedVehiclesFromSerialStrings(
		serials: string[],
		vehicles: Vehicle[]
	): void {
		if (serials?.length && vehicles?.length) {
			const selectedVehicles = vehicles.filter((vehicle) => serials.includes(vehicle.serial));
			this.value.set(selectedVehicles);
			this.selectedVehicles.set(selectedVehicles);
		}
	}

	onSave() {
		if (this.loading()) {
			return;
		}

		if (
			this.selectedVehicles().length <= this.maxSelections() &&
			this.selectedVehicles().length > 0
		) {
			// Capture current search term before saving
			const currentSearch = this.searchText()?.trim();
			if (currentSearch && currentSearch.length > 0) {
				this.addRecentSearch(currentSearch);
			}

			// catch any null / undefined values
			let vehicles = this.selectedVehicles() || [];
			this.value.set(vehicles);
			this.touch.emit();
			this.vehicleSelected.emit(vehicles);
			this.dropdown()?.closeDropdown();
		}
	}

	public toggleDropdown() {
		this.dropdown()?.toggleDropdown();
	}

	onRecentSearchClick(searchTerm: string): void {
		this.searchText.set(searchTerm);
		// Search will execute automatically via searchObservable$ with debounce
	}

	onToggleRecentSearches(): void {
		this.recentSearchesOpen.set(!this.recentSearchesOpen());
	}

	private sortOptions(response: Response<Vehicle>): Response<Vehicle> {
		const selectedSerials = this.selectedVehicles()?.map((v: Vehicle) => v.serial) || [];
		const data = response.data ?? [];
		const selectedVehicles = data.filter((vehicle) => selectedSerials.includes(vehicle.serial));
		const unselectedVehicles = data.filter((vehicle) => !selectedSerials.includes(vehicle.serial));
		return {
			...response,
			data: [...selectedVehicles, ...unselectedVehicles],
		};
	}

	focusSearchInput() {
		this.clearAndFocusSearch();
	}

	clearAndFocusSearch() {
		this.searchText.set('');
		setTimeout(() => {
			this.searchInput()?.setFocus();
		}, 100);
	}

	onTriggerFocus(): void {
		this.focused.set(true);
	}

	onTriggerBlur(): void {
		this.focused.set(false);
	}

	focusTrigger(): void {
		setTimeout(() => {
			const triggerElement = this.dropdown()?.dropdownTrigger()?.elementRef.nativeElement;
			if (triggerElement && typeof triggerElement.focus === 'function') {
				triggerElement.focus();
			}
		});
	}

	closeDropdown(): void {
		this.dropdown()?.closeDropdown();
	}

	focusFirstOption(): void {
		setTimeout(() => {
			const firstOption = this.optionsList()?.nativeElement?.querySelector(
				'union-select-option[tabindex="0"], vehicle-select-option[tabindex="0"], [role="button"][tabindex="0"]'
			) as HTMLElement;
			if (firstOption && typeof firstOption.focus === 'function') {
				firstOption.focus();
			}
		}, 0);
	}

	focusNextOption(currentElement: EventTarget | null): void {
		if (!currentElement || !(currentElement instanceof HTMLElement)) return;

		const allFocusableElements = Array.from(
			this.optionsList()?.nativeElement?.querySelectorAll(
				'union-select-option[tabindex="0"], vehicle-select-option[tabindex="0"], [role="button"][tabindex="0"]'
			) || []
		) as HTMLElement[];

		const currentIndex = allFocusableElements.indexOf(currentElement);
		if (currentIndex >= 0 && currentIndex < allFocusableElements.length - 1) {
			const nextElement = allFocusableElements[currentIndex + 1];
			if (nextElement && typeof nextElement.focus === 'function') {
				nextElement.focus();
			}
		}
	}

	focusPreviousOption(currentElement: EventTarget | null): void {
		if (!currentElement || !(currentElement instanceof HTMLElement)) return;

		const allFocusableElements = Array.from(
			this.optionsList()?.nativeElement?.querySelectorAll(
				'union-select-option[tabindex="0"], vehicle-select-option[tabindex="0"], [role="button"][tabindex="0"]'
			) || []
		) as HTMLElement[];

		const currentIndex = allFocusableElements.indexOf(currentElement);
		if (currentIndex > 0) {
			const previousElement = allFocusableElements[currentIndex - 1];
			if (previousElement && typeof previousElement.focus === 'function') {
				previousElement.focus();
			}
		}
	}

	onDropdownKeydown(event: KeyboardEvent): void {
		switch (event.key) {
			case 'Escape':
				this.closeDropdown();
				setTimeout(() => {
					const triggerElement = this.dropdown()?.dropdownTrigger()?.elementRef?.nativeElement;
					if (triggerElement && typeof triggerElement.focus === 'function') {
						triggerElement.focus();
					}
				}, 0);
				event.preventDefault();
				break;
			case 'Tab':
				if (event.shiftKey) {
					const activeElement = document.activeElement;
					const allFocusableElements = Array.from(
						this.optionsList()?.nativeElement?.querySelectorAll(
							'union-select-option[tabindex="0"], vehicle-select-option[tabindex="0"], [role="button"][tabindex="0"]'
						) || []
					) as HTMLElement[];

					if (allFocusableElements.length > 0 && activeElement === allFocusableElements[0]) {
						this.searchInput()?.setFocus();
						event.preventDefault();
						return;
					}
				}

				if (!event.shiftKey) {
					setTimeout(() => {
						const activeElement = document.activeElement;
						const dropdownContent = this.optionsList()?.nativeElement?.closest('[role="listbox"]');
						if (dropdownContent && !dropdownContent.contains(activeElement)) {
							this.closeDropdown();
						}
					}, 0);
				}
				break;
		}
	}

	focusFirstButton(): void {
		setTimeout(() => {
			const dropdownContent = this.optionsList()?.nativeElement?.closest('[role="listbox"]');
			const firstButton = dropdownContent?.querySelector(
				'[role="button"][tabindex="0"]'
			) as HTMLElement;
			if (firstButton && typeof firstButton.focus === 'function') {
				firstButton.focus();
			}
		}, 0);
	}

	focusSecondButton(): void {
		setTimeout(() => {
			// Search in the entire dropdown content, not just optionsList
			const dropdownContent = this.optionsList()?.nativeElement?.closest('[role="listbox"]');
			const allButtons = Array.from(
				dropdownContent?.querySelectorAll('[role="button"][tabindex="0"]') || []
			) as HTMLElement[];

			if (allButtons.length > 1) {
				const secondButton = allButtons[1];
				if (secondButton && typeof secondButton.focus === 'function') {
					secondButton.focus();
				}
			}
		}, 0);
	}

	onArrowDownKeydown(event: KeyboardEvent, currentElement?: EventTarget | null): void {
		if (currentElement) {
			this.focusNextOption(currentElement);
		} else {
			this.focusFirstOption();
		}
		event.preventDefault();
		event.stopPropagation();
	}

	onArrowUpKeydown(event: KeyboardEvent, currentElement: EventTarget): void {
		this.focusPreviousOption(currentElement);
		event.preventDefault();
		event.stopPropagation();
	}

	onEscapeKeydown(event: KeyboardEvent): void {
		this.closeDropdown();
		event.preventDefault();
	}

	onTabKeydown(event: KeyboardEvent, tabAction: 'first' | 'second'): void {
		if (tabAction === 'first') {
			this.focusFirstButton();
		} else {
			this.focusSecondButton();
		}
		event.preventDefault();
	}

	onSelectVehicleKeydown(event: KeyboardEvent, vehicle?: Vehicle): void {
		if (!vehicle) {
			return;
		}

		this.onSelect(vehicle);
		event.preventDefault();
	}

	onToggleUnionKeydown(event: KeyboardEvent, union: VehicleSelectOptionGroup): void {
		this.onToggleUnion(union);
		event.preventDefault();
	}

	onDeselectAllKeydown(event: KeyboardEvent): void {
		this.onDeselectAll();
		event.preventDefault();
	}

	onSaveKeydown(event: KeyboardEvent): void {
		this.onSave();
		event.preventDefault();
	}

	private loadRecentSearches(): string[] {
		try {
			const stored = this.cookieService.getCookie(Cookie.vehicleRecentSearches);
			if (!stored) return [];

			const parsed: RecentSearch[] = JSON.parse(stored);
			if (!Array.isArray(parsed)) return [];

			return parsed
				.filter((item) => item)
				.map((item) => item.term)
				.slice(0, MAX_RECENT_SEARCHES);
		} catch {
			return [];
		}
	}

	private saveRecentSearches(searches: string[]): void {
		try {
			const data: RecentSearch[] = searches.map((term) => ({
				term,
				timestamp: Date.now(),
			}));
			this.cookieService.setCookieWithValue(Cookie.vehicleRecentSearches, JSON.stringify(data));
		} catch {
			// Silent failure - feature gracefully degrades
		}
	}

	private addRecentSearch(searchTerm: string): void {
		const current = this.recentSearches();
		const filtered = current.filter((term) => term.toLowerCase() !== searchTerm.toLowerCase());
		const updated = [searchTerm, ...filtered].slice(0, MAX_RECENT_SEARCHES);

		this.recentSearches.set(updated);
		this.saveRecentSearches(updated);
	}
}
